import {
  playingStarters,
  waitlistReserves,
  suggestWinterDoppelCourtMix,
} from "./planning.js";
import { withWinterDoppelAlternatingGroupPhases } from "./groups.js";
import { generateWinterDoppelGroupDates } from "./groups.js";

export const WINTERDOPPEL_PLAN_MATRIX_CELL = {
  NONE: "none",
  STARTER: "starter",
  RESERVE: "reserve",
  HOLIDAY: "holiday",
};

export const WINTERDOPPEL_PLAN_MATRIX_PLAYERS_PER_COURT = 4;

export const WINTERDOPPEL_PLAN_MATRIX_WARNING = {
  THREE_IN_DOUBLE: "three_in_double",
  INSUFFICIENT_DOUBLES: "insufficient_doubles",
  INSUFFICIENT_PLAYERS: "insufficient_players",
  COURT_MIX_SUGGESTION: "court_mix_suggestion",
};

/** Mindestspieler für Einzel auf einem Platz (1 Doppel konfiguriert). */
export const WINTERDOPPEL_PLAN_MATRIX_MIN_SINGLES_PLAYERS = 2;

export function resolveWinterDoppelPlanMatrixCourtNo(sp) {
  const court = Number(sp?.court_no);
  if (Number.isFinite(court) && court >= 1) return court;
  return null;
}

export function inferWinterDoppelPlanMatrixCourtNo(
  starter,
  startersInSession = [],
) {
  const fromDb = resolveWinterDoppelPlanMatrixCourtNo(starter);
  if (fromDb) return fromDb;
  const sorted = [...startersInSession]
    .slice()
    .sort((a, b) =>
      String(a.player_id || "").localeCompare(String(b.player_id || "")),
    );
  const idx = sorted.findIndex((s) => s.player_id === starter.player_id);
  if (idx < 0) return 1;
  return Math.floor(idx / WINTERDOPPEL_PLAN_MATRIX_PLAYERS_PER_COURT) + 1;
}

function playerSortName(player, playerLabels) {
  return (
    playerLabels?.get?.(player.id) ||
    String(player?.display_name || "").trim() ||
    String(player?.id || "")
  );
}

function groupPlayerIds(group) {
  return (
    Array.isArray(group?.playerIds)
      ? group.playerIds
      : (group?.members || []).map((m) => m.player_id)
  )
    .map((id) => String(id || "").trim())
    .filter(Boolean);
}

/** Alle Gruppennamen für Header-Klammern (auch wenn Zeilen gefiltert sind). */
export function resolveWinterDoppelPlanMatrixGroupMemberNames({
  group,
  players = [],
  playerLabels = null,
} = {}) {
  const byId = new Map(
    (Array.isArray(players) ? players : []).map((p) => [String(p.id), p]),
  );
  const names = [];
  const seen = new Set();
  for (const id of groupPlayerIds(group)) {
    if (seen.has(id)) continue;
    seen.add(id);
    const fromLabel = playerLabels?.get?.(id);
    const fromPlayer = byId.get(id);
    const name =
      (fromLabel && String(fromLabel).trim()) ||
      String(fromPlayer?.display_name || "").trim() ||
      "";
    if (name) names.push(name);
  }
  return names.sort((a, b) =>
    a.localeCompare(b, "de", { sensitivity: "base" }),
  );
}

/**
 * Sortierung: Gruppen in Reihenfolge des ersten Termins, Mitglieder alpha;
 * Spieler ohne Gruppe ganz unten.
 * @returns {{ segments: Array<{ group: object|null, players: object[] }>, players: object[] }}
 */
export function buildWinterDoppelPlanMatrixPlayerSegments({
  players = [],
  playerGroups = [],
  playerLabels = null,
} = {}) {
  const list = Array.isArray(players) ? [...players] : [];
  if (!list.length) return { segments: [], players: [] };

  const groups = withWinterDoppelAlternatingGroupPhases(
    Array.isArray(playerGroups) ? playerGroups : [],
  );
  const withFirst = groups
    .map((g) => {
      const dates = generateWinterDoppelGroupDates({
        ...g,
        exclude_holidays: false,
      });
      return { g, first: dates[0] || "9999-99-99" };
    })
    .sort(
      (a, b) =>
        String(a.first).localeCompare(String(b.first)) ||
        String(a.g.created_at || "").localeCompare(
          String(b.g.created_at || ""),
        ) ||
        String(a.g.name || "").localeCompare(String(b.g.name || ""), "de") ||
        String(a.g.id || "").localeCompare(String(b.g.id || "")),
    );

  /** @type {Array<{ group: object|null, players: object[] }>} */
  const segments = [];
  const seen = new Set();
  for (const { g } of withFirst) {
    const memberIds = new Set(
      (Array.isArray(g.playerIds)
        ? g.playerIds
        : (g.members || []).map((m) => m.player_id)
      )
        .map((id) => String(id || "").trim())
        .filter(Boolean),
    );
    const members = list
      .filter((p) => memberIds.has(String(p.id)))
      .sort((a, b) =>
        playerSortName(a, playerLabels).localeCompare(
          playerSortName(b, playerLabels),
          "de",
        ),
      )
      .filter((p) => {
        if (seen.has(p.id)) return false;
        seen.add(p.id);
        return true;
      });
    if (members.length) segments.push({ group: g, players: members });
  }

  const rest = list
    .filter((p) => !seen.has(p.id))
    .sort((a, b) =>
      playerSortName(a, playerLabels).localeCompare(
        playerSortName(b, playerLabels),
        "de",
      ),
    );
  if (rest.length) segments.push({ group: null, players: rest });

  return {
    segments,
    players: segments.flatMap((s) => s.players),
  };
}

export function sortWinterDoppelPlayersForPlanMatrix(opts = {}) {
  return buildWinterDoppelPlanMatrixPlayerSegments(opts).players;
}

/**
 * Welche festen Gruppen spielen an diesem Termin (mind. 2 Starter aus der Gruppe).
 */
export function resolveWinterDoppelGroupsPlaying({
  starters = [],
  playerGroups = [],
} = {}) {
  const starterIds = new Set(
    (starters || [])
      .map((sp) => String(sp?.player_id || "").trim())
      .filter(Boolean),
  );
  if (!starterIds.size) return [];
  const hits = [];
  for (const g of playerGroups || []) {
    const members = (
      Array.isArray(g.playerIds)
        ? g.playerIds
        : (g.members || []).map((m) => m.player_id)
    )
      .map((id) => String(id || "").trim())
      .filter(Boolean);
    if (!members.length) continue;
    let overlap = 0;
    for (const id of members) {
      if (starterIds.has(id)) overlap += 1;
    }
    if (overlap >= 2 || (members.length === 1 && overlap === 1)) {
      hits.push(g);
    }
  }
  return hits;
}

/** Spieler ohne Starter-Einsatz in der aktuellen Grobplanung. */
export function listWinterDoppelPlayersWithoutStarts({
  players = [],
  sessionPlayers = [],
} = {}) {
  const starterIds = new Set();
  for (const sp of sessionPlayers || []) {
    if (sp?.role === "starter" && sp.player_id) {
      starterIds.add(String(sp.player_id));
    }
  }
  return (players || []).filter((p) => p?.id && !starterIds.has(String(p.id)));
}

/**
 * @param {{
 *   players?: object[],
 *   rosterPlayers?: object[],
 *   sessions?: object[],
 *   sessionPlayers?: object[],
 *   playerLabels?: Map<string, string>|null,
 *   holidayDates?: Array<{ date: string, name?: string }>,
 *   playerGroups?: object[],
 * }} opts
 */
export function buildWinterDoppelPlanMatrix({
  players = [],
  rosterPlayers = null,
  sessions = [],
  sessionPlayers = [],
  playerLabels = null,
  holidayDates = [],
  playerGroups = [],
  selfServiceByPlayerId = null,
} = {}) {
  const nameRoster =
    Array.isArray(rosterPlayers) && rosterPlayers.length
      ? rosterPlayers
      : players;
  const activeSessions = (sessions || [])
    .filter((s) => s && s.status !== "cancelled")
    .slice()
    .sort((a, b) =>
      String(a.session_date || "").localeCompare(String(b.session_date || "")),
    );

  const holidayByDate = new Map();
  for (const h of holidayDates || []) {
    const d = String(h?.date || "").slice(0, 10);
    if (d) {
      holidayByDate.set(d, {
        name: String(h?.name || "").trim(),
        source: String(h?.source || "holiday"),
      });
    }
  }

  const firstSessionDate =
    activeSessions
      .map((s) => String(s.session_date || "").slice(0, 10))
      .filter(Boolean)
      .sort()[0] || "";

  const byDate = new Map();
  for (const s of activeSessions) {
    const d = String(s.session_date || "").slice(0, 10);
    if (!d) continue;
    if (holidayByDate.has(d)) {
      const hol = holidayByDate.get(d);
      byDate.set(d, {
        id: `holiday:${d}`,
        session_date: d,
        status: "holiday",
        isHoliday: true,
        holidayName: hol.name || "",
        holidaySource: hol.source || "holiday",
      });
    } else {
      byDate.set(d, s);
    }
  }
  for (const [d, hol] of holidayByDate) {
    if (byDate.has(d)) continue;
    if (firstSessionDate && d < firstSessionDate) continue;
    byDate.set(d, {
      id: `holiday:${d}`,
      session_date: d,
      status: "holiday",
      isHoliday: true,
      holidayName: hol.name || "",
      holidaySource: hol.source || "holiday",
    });
  }

  const columns = [...byDate.values()].sort((a, b) =>
    String(a.session_date || "").localeCompare(String(b.session_date || "")),
  );

  const bySession = new Map();
  for (const sp of sessionPlayers || []) {
    if (!sp?.session_id) continue;
    const list = bySession.get(sp.session_id) || [];
    list.push(sp);
    bySession.set(sp.session_id, list);
  }

  const assignmentByKey = new Map();
  for (const session of columns) {
    if (session.isHoliday) continue;
    const sps = bySession.get(session.id) || [];
    const starters = playingStarters(sps);
    for (const sp of starters) {
      const slot = Number(sp.pair_slot);
      assignmentByKey.set(`${sp.player_id}:${session.id}`, {
        kind: WINTERDOPPEL_PLAN_MATRIX_CELL.STARTER,
        courtNo: inferWinterDoppelPlanMatrixCourtNo(sp, starters),
        pairSlot:
          Number.isFinite(slot) && (slot === 0 || slot === 1) ? slot : null,
      });
    }
    for (const sp of waitlistReserves(sps)) {
      assignmentByKey.set(`${sp.player_id}:${session.id}`, {
        kind: WINTERDOPPEL_PLAN_MATRIX_CELL.RESERVE,
      });
    }
  }

  const { segments } = buildWinterDoppelPlanMatrixPlayerSegments({
    players,
    playerGroups,
    playerLabels,
  });

  const showGroupHeaders = segments.some((s) => s.group);
  /** @type {object[]} */
  const rows = [];
  let groupIndex = 0;
  for (const seg of segments) {
    if (showGroupHeaders && seg.group) {
      groupIndex += 1;
      rows.push({
        kind: "groupHeader",
        groupId: String(seg.group.id || ""),
        groupName:
          String(seg.group.name || "").trim() || `Gruppe ${groupIndex}`,
        groupIndex,
        memberNames: resolveWinterDoppelPlanMatrixGroupMemberNames({
          group: seg.group,
          players: nameRoster,
          playerLabels,
        }),
      });
    } else if (showGroupHeaders && !seg.group && seg.players.length) {
      rows.push({
        kind: "groupHeader",
        groupId: "",
        groupName: "",
        groupIndex: 0,
        ungrouped: true,
      });
    }
    for (const p of seg.players) {
      const pid = String(p.id || "");
      rows.push({
        kind: "player",
        playerId: p.id,
        name:
          playerLabels?.get?.(p.id) ||
          String(p.display_name || "").trim() ||
          p.id,
        selfService:
          selfServiceByPlayerId?.get?.(pid) ?? Boolean(p.self_service_at),
        cells: columns.map((s) => {
          if (s.isHoliday) {
            return {
              kind: WINTERDOPPEL_PLAN_MATRIX_CELL.HOLIDAY,
              holidayName: s.holidayName || "",
            };
          }
          const cell = assignmentByKey.get(`${p.id}:${s.id}`);
          return cell || { kind: WINTERDOPPEL_PLAN_MATRIX_CELL.NONE };
        }),
      });
    }
  }

  return { sessions: columns, rows };
}

/**
 * Starter je Doppel/Platz (court_no oder aus Reihenfolge abgeleitet).
 * @returns {Map<number, number>}
 */
export function countWinterDoppelStartersPerCourt(sessionPlayers = []) {
  const starters = playingStarters(sessionPlayers);
  const sorted = [...starters].sort((a, b) =>
    String(a.player_id || "").localeCompare(String(b.player_id || "")),
  );
  const counts = new Map();
  for (const sp of starters) {
    const court = inferWinterDoppelPlanMatrixCourtNo(sp, sorted);
    counts.set(court, (counts.get(court) || 0) + 1);
  }
  return counts;
}

function shouldWarnThreeInDouble({
  courtStarters,
  totalStarters,
  totalReserves,
}) {
  if (courtStarters !== 3) return false;
  return courtStarters + totalReserves < 4;
}

function countCourtsBySize(perCourt) {
  let doubles = 0;
  let singles = 0;
  let odd = 0;
  for (const count of perCourt.values()) {
    if (count === 4) doubles += 1;
    else if (count === 2) singles += 1;
    else if (count > 0) odd += 1;
  }
  return { doubles, singles, odd };
}

/**
 * Deckungslücken je Termin (Doppel vs. Einzel, Mix-Vorschlag).
 * @returns {Array<{ kind: string, courtNo?: number, courts?: number, needed?: number, pool?: number, doubles?: number, singles?: number, waitlist?: number, unusedCourts?: number }>}
 */
export function analyzeWinterDoppelSessionCoverage({
  sessionPlayers = [],
  courtCount = 1,
} = {}) {
  const courts = Math.max(1, Number(courtCount) || 1);
  const neededForDoubles = courts * WINTERDOPPEL_PLAN_MATRIX_PLAYERS_PER_COURT;
  const starters = playingStarters(sessionPlayers);
  const reserves = waitlistReserves(sessionPlayers);
  const pool = starters.length + reserves.length;
  if (!pool) return [];

  const mix = suggestWinterDoppelCourtMix({ pool, courtCount: courts });

  /** @type {Array<{ kind: string, courtNo?: number, courts?: number, needed?: number, pool?: number, doubles?: number, singles?: number, waitlist?: number, unusedCourts?: number }>} */
  const warnings = [];

  if (mix.playing === 0) {
    warnings.push({
      kind: WINTERDOPPEL_PLAN_MATRIX_WARNING.INSUFFICIENT_PLAYERS,
      courts,
      needed: neededForDoubles,
      pool,
    });
    return warnings;
  }

  const perCourt = countWinterDoppelStartersPerCourt(sessionPlayers);
  const layout = countCourtsBySize(perCourt);
  const layoutMatches =
    starters.length === mix.playing &&
    layout.doubles === mix.doubles &&
    layout.singles === mix.singles &&
    layout.odd === 0 &&
    reserves.length === mix.waitlist;

  if (layoutMatches) return [];

  const needsMixHint =
    pool < neededForDoubles ||
    layout.odd > 0 ||
    starters.length !== mix.playing ||
    layout.doubles !== mix.doubles ||
    layout.singles !== mix.singles;

  if (needsMixHint) {
    warnings.push({
      kind: WINTERDOPPEL_PLAN_MATRIX_WARNING.COURT_MIX_SUGGESTION,
      courts,
      pool,
      needed: neededForDoubles,
      doubles: mix.doubles,
      singles: mix.singles,
      waitlist: mix.waitlist,
      unusedCourts: mix.unusedCourts,
    });
    return warnings;
  }

  for (const [courtNo, count] of perCourt) {
    if (
      shouldWarnThreeInDouble({
        courtStarters: count,
        totalStarters: starters.length,
        totalReserves: reserves.length,
      })
    ) {
      warnings.push({
        kind: WINTERDOPPEL_PLAN_MATRIX_WARNING.THREE_IN_DOUBLE,
        courtNo,
      });
    }
  }

  return warnings;
}

/**
 * @param {{
 *   sessions?: object[],
 *   sessionPlayers?: object[],
 *   courtCount?: number,
 * }} opts
 * @returns {Array<{ sessionId: string, sessionDate: string, warnings: object[] }>}
 */
export function buildWinterDoppelPlanMatrixWarnings({
  sessions = [],
  sessionPlayers = [],
  courtCount = 1,
} = {}) {
  const bySession = new Map();
  for (const sp of sessionPlayers || []) {
    if (!sp?.session_id) continue;
    const list = bySession.get(sp.session_id) || [];
    list.push(sp);
    bySession.set(sp.session_id, list);
  }

  const out = [];
  for (const session of sessions || []) {
    if (!session?.id || session.isHoliday || session.status === "cancelled") {
      continue;
    }
    const warnings = analyzeWinterDoppelSessionCoverage({
      sessionPlayers: bySession.get(session.id) || [],
      courtCount,
    });
    if (!warnings.length) continue;
    out.push({
      sessionId: String(session.id),
      sessionDate: String(session.session_date || "").slice(0, 10),
      warnings,
    });
  }
  return out;
}

export const WINTERDOPPEL_PAIR_RELATION = {
  PARTNERS: "partners",
  OPPONENTS: "opponents",
  SAME_COURT: "same_court",
  OTHER_COURT: "other_court",
};

/** Filtertext → Tokens (Leerzeichen, Komma, &, +). */
export function parseWinterDoppelNameFilterTokens(filterText) {
  return String(filterText || "")
    .toLocaleLowerCase("de")
    .split(/[,;&+\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Ordnet Tokens Spielerzeilen zu (je Token höchstens ein Treffer).
 * @param {object[]} rows Matrix-Rows
 * @param {string} filterText
 */
export function matchWinterDoppelMatrixPlayersByFilter(rows = [], filterText) {
  const tokens = parseWinterDoppelNameFilterTokens(filterText);
  const playerRows = (rows || []).filter(
    (r) => r?.kind === "player" && r.playerId,
  );
  if (!tokens.length) {
    return { tokens, matched: [], unmatchedTokens: [] };
  }
  const used = new Set();
  const matched = [];
  const unmatchedTokens = [];
  for (const tok of tokens) {
    const hits = playerRows
      .filter((r) => {
        if (used.has(String(r.playerId))) return false;
        return String(r.name || "")
          .toLocaleLowerCase("de")
          .includes(tok);
      })
      .sort((a, b) => {
        const na = String(a.name || "").toLocaleLowerCase("de");
        const nb = String(b.name || "").toLocaleLowerCase("de");
        const aStarts = na.startsWith(tok) ? 0 : 1;
        const bStarts = nb.startsWith(tok) ? 0 : 1;
        if (aStarts !== bStarts) return aStarts - bStarts;
        if (na.length !== nb.length) return na.length - nb.length;
        return na.localeCompare(nb, "de");
      });
    if (!hits.length) {
      unmatchedTokens.push(tok);
      continue;
    }
    matched.push(hits[0]);
    used.add(String(hits[0].playerId));
  }
  return { tokens, matched, unmatchedTokens };
}

function __starterOnSession(sessionPlayers, playerId) {
  const pid = String(playerId || "");
  return (sessionPlayers || []).find(
    (sp) =>
      String(sp?.player_id || "") === pid &&
      sp?.role === "starter" &&
      sp?.rsvp !== "no",
  );
}

/**
 * @returns {typeof WINTERDOPPEL_PAIR_RELATION[keyof typeof WINTERDOPPEL_PAIR_RELATION]|null}
 */
export function relateWinterDoppelStartersOnCourt(spA, spB) {
  if (!spA || !spB) return null;
  const courtA = resolveWinterDoppelPlanMatrixCourtNo(spA);
  const courtB = resolveWinterDoppelPlanMatrixCourtNo(spB);
  const cA =
    courtA ||
    inferWinterDoppelPlanMatrixCourtNo(spA, [spA, spB].filter(Boolean));
  const cB =
    courtB ||
    inferWinterDoppelPlanMatrixCourtNo(spB, [spA, spB].filter(Boolean));
  if (cA !== cB) return WINTERDOPPEL_PAIR_RELATION.OTHER_COURT;
  const slotA = Number(spA.pair_slot);
  const slotB = Number(spB.pair_slot);
  if (
    Number.isFinite(slotA) &&
    Number.isFinite(slotB) &&
    (slotA === 0 || slotA === 1) &&
    (slotB === 0 || slotB === 1)
  ) {
    return slotA === slotB
      ? WINTERDOPPEL_PAIR_RELATION.PARTNERS
      : WINTERDOPPEL_PAIR_RELATION.OPPONENTS;
  }
  return WINTERDOPPEL_PAIR_RELATION.SAME_COURT;
}

/**
 * Treffen zweier Spieler über die Saison (Partner / Gegner / anderer Platz).
 */
export function analyzeWinterDoppelPlayerPairMeetings({
  playerIdA,
  playerIdB,
  sessions = [],
  sessionPlayers = [],
} = {}) {
  const idA = String(playerIdA || "");
  const idB = String(playerIdB || "");
  const empty = {
    partners: 0,
    opponents: 0,
    sameCourt: 0,
    otherCourt: 0,
    meetings: [],
    bySessionId: new Map(),
  };
  if (!idA || !idB || idA === idB) return empty;

  const bySession = new Map();
  for (const sp of sessionPlayers || []) {
    if (!sp?.session_id) continue;
    const list = bySession.get(sp.session_id) || [];
    list.push(sp);
    bySession.set(sp.session_id, list);
  }

  const meetings = [];
  const bySessionId = new Map();
  let partners = 0;
  let opponents = 0;
  let sameCourt = 0;
  let otherCourt = 0;

  for (const session of sessions || []) {
    if (!session?.id || session.isHoliday || session.status === "cancelled") {
      continue;
    }
    const sps = bySession.get(session.id) || [];
    const a = __starterOnSession(sps, idA);
    const b = __starterOnSession(sps, idB);
    if (!a || !b) continue;
    const relation = relateWinterDoppelStartersOnCourt(a, b);
    if (!relation) continue;
    const row = {
      sessionId: String(session.id),
      sessionDate: String(session.session_date || "").slice(0, 10),
      relation,
      courtNo:
        resolveWinterDoppelPlanMatrixCourtNo(a) ||
        resolveWinterDoppelPlanMatrixCourtNo(b),
    };
    meetings.push(row);
    bySessionId.set(row.sessionId, row);
    if (relation === WINTERDOPPEL_PAIR_RELATION.PARTNERS) partners += 1;
    else if (relation === WINTERDOPPEL_PAIR_RELATION.OPPONENTS) opponents += 1;
    else if (relation === WINTERDOPPEL_PAIR_RELATION.SAME_COURT) sameCourt += 1;
    else otherCourt += 1;
  }

  return { partners, opponents, sameCourt, otherCourt, meetings, bySessionId };
}

/**
 * Matrix-Zeilen für Namensfilter; bei genau 2 Treffern Paar-Analyse anhängen.
 */
export function filterWinterDoppelPlanMatrixRows({
  rows = [],
  filterText = "",
  sessions = [],
  sessionPlayers = [],
} = {}) {
  const { tokens, matched, unmatchedTokens } =
    matchWinterDoppelMatrixPlayersByFilter(rows, filterText);
  if (!tokens.length) {
    return {
      rows,
      tokens,
      matched: [],
      unmatchedTokens: [],
      pairAnalysis: null,
    };
  }
  const matchedIds = new Set(matched.map((r) => String(r.playerId)));
  const out = [];
  let pendingHeader = null;
  for (const row of rows) {
    if (row.kind === "groupHeader") {
      pendingHeader = row;
      continue;
    }
    if (!matchedIds.has(String(row.playerId))) continue;
    if (pendingHeader) {
      out.push(pendingHeader);
      pendingHeader = null;
    }
    out.push(row);
  }
  const pairAnalysis =
    matched.length === 2
      ? analyzeWinterDoppelPlayerPairMeetings({
          playerIdA: matched[0].playerId,
          playerIdB: matched[1].playerId,
          sessions,
          sessionPlayers,
        })
      : null;
  return { rows: out, tokens, matched, unmatchedTokens, pairAnalysis };
}
